// Product Control Plane — Local AI models, the CLIENT side of component type `local_ai_models` (product `gao-local-ai`).
//
// A deliberate re-implementation of gao-id-worker's `src/lib/control-plane.local-ai.ts` and the `local_ai_models` rules of
// its contract (docs/control-plane/CONTROL_PLANE_SCHEMA_V1.md §9), for the same reason `contract.ts` re-implements the
// catalog rules: the console must be able to say, BEFORE a save, which model row the server will refuse and why.
//
// One item is one model RELEASE: the immutable `gao.local-ai.release/v1` record the Gao App downloads and verifies (the
// package's manifest digest and every file's SHA-256 and size), plus its publication `state` on this lane. The console
// edits what an operator decides — the name and one-line description people read, the order, the environment, the state
// and its reason — and never the package: a changed package is a new release, added from its own record.

import type { ItemEnvironment } from "./contract";

export const PRODUCT_GAO_LOCAL_AI = "gao-local-ai" as const;
export const COMPONENT_TYPE_LOCAL_AI_MODELS = "local_ai_models" as const;
export const RELEASE_SCHEMA = "gao.local-ai.release/v1" as const;

export const LOCAL_AI_STATES = ["STAGED", "PUBLISHED", "DEPRECATED", "REVOKED"] as const;
export type LocalAIState = (typeof LOCAL_AI_STATES)[number];
export const REASON_STATES: readonly LocalAIState[] = ["DEPRECATED", "REVOKED"];

/** What each state means on a person's device — shown beside the state picker. */
export const STATE_TEXT: Record<LocalAIState, string> = {
  STAGED: "test release — visible on DEV/TEST only, never Product",
  PUBLISHED: "offered for Download",
  DEPRECATED: "no new Downloads; an installed copy keeps working",
  REVOKED: "no new Downloads and no new answers from an installed copy; it is never deleted or switched",
};

export interface LocalAIRelease {
  modelId: string;
  version: string;
  presentation: { key: string; displayName: string; shortDescription: string; order: number; preference: number };
  package: {
    packageId: string;
    packageVersion: string;
    downloadBytes: number;
    installedBytes: number;
    manifest: { bytes: number; sha256: string };
    files: Array<{ path: string; bytes: number; sha256: string; parts?: Array<{ bytes: number; sha256: string }> }>;
  };
  provenance: { upstreamModelId: string; upstreamRevision: string; license: string; [k: string]: unknown };
  capabilities: string[];
  runtime: { abi: string; [k: string]: unknown };
  compatibility: { minClientLevel: number; platforms: string[]; memoryClassBytes: number };
  [member: string]: unknown;
}

export interface LocalAIModelItem {
  id: string;
  environment: ItemEnvironment;
  state: LocalAIState;
  reason: string | null;
  release: LocalAIRelease;
}

export interface LocalAIModelsComponent {
  type: typeof COMPONENT_TYPE_LOCAL_AI_MODELS;
  id: string;
  items: LocalAIModelItem[];
}

export const ITEM_ID_RE = /^[a-z0-9][a-z0-9._-]{0,127}$/;
const MODEL_ID_RE = /^[a-z0-9][a-z0-9._-]{2,63}$/;
const SEMVER_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*)(?:\.(?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*))*))?$/;
const PRESENTATION_KEY_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;
const HEX40_RE = /^[0-9a-f]{40}$/;
const HEX64_RE = /^[0-9a-f]{64}$/;
const FILE_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\u0000-\u001F\u007F-\u009F]/;
const PART_BYTES = 134_217_728;
const CAPABILITIES = ["chat", "compiler", "private_context_answering", "structured_generation"];
const LICENSES = ["Apache-2.0", "MIT"];
const PLATFORMS = ["android", "ios"];
const WORKLOADS = ["realistic-use", "synthetic-stress"];
const OUTCOMES = ["not-recommended", "passed", "unsupported"];
export const REASON_MAX = 500;

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** Every violation of the release law, `path: message` — the server's rule, re-implemented. */
export function releaseProblems(r: unknown, path = "release"): string[] {
  const out: string[] = [];
  const fail = (p: string, m: string) => {
    out.push(`${p}: ${m}`);
    return false;
  };
  const obj = (v: unknown, p: string, req: string[], opt: string[] = []): v is Record<string, unknown> => {
    if (!isObject(v)) return fail(p, "must be an object");
    const allowed = new Set([...req, ...opt]);
    for (const k of Object.keys(v)) if (!allowed.has(k)) fail(`${p}.${k}`, "is not a member of this object");
    let ok = true;
    for (const k of req) if (!has(v, k)) ok = fail(`${p}.${k}`, "is required");
    return ok;
  };
  const str = (v: unknown, p: string, o: { max?: number; re?: RegExp; oneOf?: string[] } = {}): v is string => {
    if (typeof v !== "string" || v.length === 0) return fail(p, "must be a non-empty string");
    if (o.max !== undefined && [...v].length > o.max) return fail(p, `at most ${o.max} characters`);
    if (CONTROL_CHAR_RE.test(v)) return fail(p, "must not contain control characters");
    if (o.re && !o.re.test(v)) return fail(p, "is not in the expected form");
    if (o.oneOf && !o.oneOf.includes(v)) return fail(p, `must be one of ${o.oneOf.join(", ")}`);
    return true;
  };
  const int = (v: unknown, p: string, min = -Number.MAX_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER): v is number => {
    if (typeof v !== "number" || !Number.isSafeInteger(v)) return fail(p, "must be an integer");
    if (v < min || v > max) return fail(p, `must be in [${min}, ${max}]`);
    return true;
  };
  const sortedSet = (v: unknown, p: string, vocab: string[]) => {
    if (!Array.isArray(v) || v.length === 0) return fail(p, "must be a non-empty array");
    v.forEach((x, i) => {
      if (str(x, `${p}[${i}]`, { oneOf: vocab }) && i > 0 && !((v[i - 1] as string) < x)) fail(p, "must be sorted and unique");
    });
    return true;
  };

  if (!obj(r, path, ["modelId", "version", "presentation", "provenance", "capabilities", "runtime", "package", "compatibility", "recommendation"])) {
    return out;
  }
  str(r.modelId, `${path}.modelId`, { re: MODEL_ID_RE });
  str(r.version, `${path}.version`, { re: SEMVER_RE });
  const pr = r.presentation;
  if (obj(pr, `${path}.presentation`, ["key", "displayName", "shortDescription", "order", "preference"])) {
    str(pr.key, `${path}.presentation.key`, { re: PRESENTATION_KEY_RE });
    str(pr.displayName, `${path}.presentation.displayName`, { max: 40 });
    str(pr.shortDescription, `${path}.presentation.shortDescription`, { max: 120 });
    int(pr.order, `${path}.presentation.order`);
    int(pr.preference, `${path}.presentation.preference`);
  }
  const pv = r.provenance;
  if (obj(pv, `${path}.provenance`, ["upstreamOwner", "upstreamOwnerName", "upstreamModelId", "upstreamRevision", "family", "parameters", "license", "licenseReference", "licenseDetails", "packageLockSha256"])) {
    for (const k of ["upstreamOwner", "upstreamOwnerName", "upstreamModelId", "family", "parameters", "licenseDetails"]) {
      str(pv[k], `${path}.provenance.${k}`, { max: 2000 });
    }
    str(pv.upstreamRevision, `${path}.provenance.upstreamRevision`, { re: HEX40_RE });
    str(pv.license, `${path}.provenance.license`, { oneOf: LICENSES });
    if (str(pv.licenseReference, `${path}.provenance.licenseReference`, { max: 2048 })) {
      let u: URL | null = null;
      try {
        u = new URL(pv.licenseReference as string);
      } catch {
        u = null;
      }
      if (!u || u.protocol !== "https:" || !u.hostname || u.username !== "" || u.password !== "") {
        fail(`${path}.provenance.licenseReference`, "must be an https:// URL without credentials");
      }
    }
    str(pv.packageLockSha256, `${path}.provenance.packageLockSha256`, { re: HEX64_RE });
  }
  sortedSet(r.capabilities, `${path}.capabilities`, CAPABILITIES);
  const rt = r.runtime;
  const RT = ["abi", "onnxRuntime", "architecture", "format", "quantization", "tokenizer", "chatTemplate", "generationContract", "packageSchema"];
  if (obj(rt, `${path}.runtime`, [...RT, "contextLength"])) {
    for (const k of RT) str(rt[k], `${path}.runtime.${k}`, { max: 200 });
    int(rt.contextLength, `${path}.runtime.contextLength`, 1);
  }
  const g = r.package;
  if (obj(g, `${path}.package`, ["packageId", "packageVersion", "downloadBytes", "installedBytes", "manifest", "files"])) {
    if (str(g.packageId, `${path}.package.packageId`, { re: MODEL_ID_RE }) && g.packageId !== r.modelId) {
      fail(`${path}.package.packageId`, "must equal modelId (a release IS its package)");
    }
    if (str(g.packageVersion, `${path}.package.packageVersion`, { re: SEMVER_RE }) && g.packageVersion !== r.version) {
      fail(`${path}.package.packageVersion`, "must equal version (a release IS its package)");
    }
    let total = 0;
    let totalOk = true;
    if (obj(g.manifest, `${path}.package.manifest`, ["bytes", "sha256"])) {
      if (int(g.manifest.bytes, `${path}.package.manifest.bytes`, 1, PART_BYTES)) total += g.manifest.bytes as number;
      else totalOk = false;
      str(g.manifest.sha256, `${path}.package.manifest.sha256`, { re: HEX64_RE });
    } else totalOk = false;
    if (Array.isArray(g.files) && g.files.length > 0) {
      const names = new Set<string>();
      g.files.forEach((f, i) => {
        const q = `${path}.package.files[${i}]`;
        if (!obj(f, q, ["path", "bytes", "sha256"], ["parts"])) {
          totalOk = false;
          return;
        }
        if (str(f.path, `${q}.path`, { re: FILE_NAME_RE })) {
          if (f.path === "manifest.json") fail(`${q}.path`, "the manifest is not a package file");
          if (names.has(f.path as string)) fail(`${q}.path`, "is named twice");
          names.add(f.path as string);
        }
        str(f.sha256, `${q}.sha256`, { re: HEX64_RE });
        if (!int(f.bytes, `${q}.bytes`, 0)) {
          totalOk = false;
          return;
        }
        total += f.bytes as number;
        if (!has(f, "parts")) {
          if ((f.bytes as number) > PART_BYTES) fail(`${q}.bytes`, "a file without parts is one origin object (≤ 128 MiB)");
          return;
        }
        if (!Array.isArray(f.parts) || f.parts.length < 2) {
          fail(`${q}.parts`, "must hold at least 2 parts");
          return;
        }
        let sum = 0;
        let partsOk = true;
        f.parts.forEach((p, j) => {
          if (!obj(p, `${q}.parts[${j}]`, ["bytes", "sha256"])) {
            partsOk = false;
            return;
          }
          if (int(p.bytes, `${q}.parts[${j}].bytes`, 1, PART_BYTES)) sum += p.bytes as number;
          else partsOk = false;
          str(p.sha256, `${q}.parts[${j}].sha256`, { re: HEX64_RE });
        });
        if (partsOk && sum !== f.bytes) fail(`${q}.parts`, `their sizes sum to ${sum}, not the file's ${f.bytes as number} bytes`);
      });
    } else {
      fail(`${path}.package.files`, "must be a non-empty array");
      totalOk = false;
    }
    const dl = int(g.downloadBytes, `${path}.package.downloadBytes`, 1);
    const inst = int(g.installedBytes, `${path}.package.installedBytes`, 1);
    if (totalOk && dl && g.downloadBytes !== total) fail(`${path}.package.downloadBytes`, `must be manifest + files = ${total}`);
    if (totalOk && inst && g.installedBytes !== total) fail(`${path}.package.installedBytes`, `must be manifest + files = ${total}`);
  }
  const cm = r.compatibility;
  if (obj(cm, `${path}.compatibility`, ["minClientLevel", "platforms", "memoryClassBytes"])) {
    int(cm.minClientLevel, `${path}.compatibility.minClientLevel`, 1);
    sortedSet(cm.platforms, `${path}.compatibility.platforms`, PLATFORMS);
    int(cm.memoryClassBytes, `${path}.compatibility.memoryClassBytes`, 1);
  }
  const rec = r.recommendation;
  if (obj(rec, `${path}.recommendation`, ["records"])) {
    if (!Array.isArray(rec.records)) fail(`${path}.recommendation.records`, "must be an array");
    else
      rec.records.forEach((x, i) => {
        const q = `${path}.recommendation.records[${i}]`;
        if (!obj(x, q, ["platform", "hardware", "memoryClassBytes", "workload", "outcome", "summary", "measurement", "evidence"], ["soc"])) return;
        str(x.platform, `${q}.platform`, { oneOf: PLATFORMS });
        if (!Array.isArray(x.hardware) || x.hardware.length === 0) fail(`${q}.hardware`, "must be a non-empty array");
        else x.hardware.forEach((h, j) => str(h, `${q}.hardware[${j}]`, { max: 200 }));
        if (has(x, "soc")) str(x.soc, `${q}.soc`, { max: 200 });
        int(x.memoryClassBytes, `${q}.memoryClassBytes`, 1);
        str(x.workload, `${q}.workload`, { oneOf: WORKLOADS });
        str(x.outcome, `${q}.outcome`, { oneOf: OUTCOMES });
        for (const k of ["summary", "measurement", "evidence"]) str(x[k], `${q}.${k}`, { max: 2000 });
      });
  }
  return out;
}

export function packageNameOf(r: Pick<LocalAIRelease, "modelId" | "version">): string {
  return `${r.modelId}-${r.version}`;
}

/** Why an item's state/reason/environment combination is refused, or null. */
export function stateProblem(item: Pick<LocalAIModelItem, "state" | "reason" | "environment">): string | null {
  if (!(LOCAL_AI_STATES as readonly string[]).includes(item.state)) return `state must be one of ${LOCAL_AI_STATES.join(", ")}`;
  if (item.state === "STAGED" && item.environment === "production") {
    return "a STAGED release is a test release: its environment is staging or test, never production";
  }
  const needs = REASON_STATES.includes(item.state);
  const reason = item.reason === null ? "" : item.reason;
  if (needs && reason.trim() === "") return `${item.state} needs a reason — it is what a person is told`;
  if (!needs && reason !== "") return `${item.state} carries no reason`;
  if ([...reason].length > REASON_MAX) return `reason: at most ${REASON_MAX} characters`;
  if (CONTROL_CHAR_RE.test(reason)) return "reason must not contain control characters";
  return null;
}

/** Every problem of every model row, in the editor's FieldIssue form (`itemId`, `field`, `message`). */
export function modelRowProblems(items: LocalAIModelItem[]): Array<{ itemId: string; field: string; message: string }> {
  const out: Array<{ itemId: string; field: string; message: string }> = [];
  const seen = new Set<string>();
  for (const it of items) {
    if (seen.has(it.id)) out.push({ itemId: it.id, field: "row", message: "added twice — one release is one row" });
    seen.add(it.id);
    if (!ITEM_ID_RE.test(it.id)) out.push({ itemId: it.id, field: "id", message: "not a package name" });
    for (const p of releaseProblems(it.release)) out.push({ itemId: it.id, field: "release", message: p });
    if (isObject(it.release) && it.id !== packageNameOf(it.release)) {
      out.push({ itemId: it.id, field: "id", message: `must be the release's package name ${packageNameOf(it.release)}` });
    }
    const s = stateProblem(it);
    if (s) out.push({ itemId: it.id, field: "state", message: s });
  }
  return out;
}

export type RecordParse = { ok: true; release: LocalAIRelease } | { ok: false; problems: string[] };

/**
 * "Add model": the text an operator pastes — a `gao.local-ai.release/v1` record as `model-origin.py release` writes it
 * (`{"schema": …, "release": {…}}`), or its bare `release` object. Held to the release law before it becomes a row.
 */
export function parseReleaseRecord(text: string): RecordParse {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch (e) {
    return { ok: false, problems: [`not JSON: ${e instanceof Error ? e.message : String(e)}`] };
  }
  let release: unknown = v;
  if (isObject(v) && has(v, "schema")) {
    if (v.schema !== RELEASE_SCHEMA) return { ok: false, problems: [`schema must be ${RELEASE_SCHEMA}`] };
    const keys = Object.keys(v).sort().join(",");
    if (keys !== "release,schema") return { ok: false, problems: ["a record holds exactly schema and release"] };
    release = v.release;
  }
  const problems = releaseProblems(release);
  return problems.length ? { ok: false, problems } : { ok: true, release: release as LocalAIRelease };
}

/** 1.55 GB — the size a person reads before a Download. */
export function humanBytes(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} MB`;
  return `${n} bytes`;
}

/** presentation.order ascending, ties by id — the order the Gao App lists them. */
export function inModelOrder(items: LocalAIModelItem[]): LocalAIModelItem[] {
  return [...items].sort((a, b) =>
    a.release.presentation.order !== b.release.presentation.order
      ? a.release.presentation.order - b.release.presentation.order
      : a.id < b.id
        ? -1
        : a.id > b.id
          ? 1
          : 0,
  );
}

/**
 * The states a row may move to from the state it was LOADED in. REVOKED is final (a new release replaces it); a
 * DEPRECATED release can only be revoked; a PUBLISHED one deprecated or revoked; a test release (or a row added in this
 * draft) staged or published. Held here so an operator cannot quietly bring a withdrawn model back.
 */
export function allowedStates(loaded: LocalAIState | null): readonly LocalAIState[] {
  switch (loaded) {
    case "REVOKED":
      return ["REVOKED"];
    case "DEPRECATED":
      return ["DEPRECATED", "REVOKED"];
    case "PUBLISHED":
      return ["PUBLISHED", "DEPRECATED", "REVOKED"];
    default:
      return ["STAGED", "PUBLISHED"];
  }
}
